import React, { useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import {
  Upload,
  X,
  CheckCircle,
  AlertCircle,
  Plus,
  Trash2,
  Image as ImageIcon,
  Music,
} from "lucide-react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { createCategory, getAllCategories } from "../../redux/reduxSlices/manageCategorySlice";
import toast from "react-hot-toast";

const CreateCategory = () => {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      name: "", // Add name field
      languages: [
        {
          language: "",
          customLanguage: "",
          files: [],
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "languages",
  });

  const [fileLists, setFileLists] = useState({});
  const [songGenres, setSongGenres] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const dispatch = useDispatch();
  const { loading, success, createCategoryResponse } = useSelector(
    (state) => state.manageCategory,
  );

  const watchedLanguages = watch("languages");

  // Function to check if language is duplicate
  const isLanguageDuplicate = (
    languageIndex,
    selectedLanguage,
    customLanguage = "",
  ) => {
    const currentLanguageValue =
      selectedLanguage === "other"
        ? customLanguage.toLowerCase().trim()
        : selectedLanguage.toLowerCase().trim();

    if (!currentLanguageValue) return false;

    return fields.some((field, index) => {
      if (index === languageIndex) return false;

      const existingLanguage = watchedLanguages[index]?.language;
      const existingCustomLanguage = watchedLanguages[index]?.customLanguage;

      const existingLanguageValue =
        existingLanguage === "other"
          ? (existingCustomLanguage || "").toLowerCase().trim()
          : (existingLanguage || "").toLowerCase().trim();

      return existingLanguageValue === currentLanguageValue;
    });
  };

  // Function to check if language is empty or invalid
  const getLanguageValue = (lang, customLang) => {
    if (lang === "other") {
      return customLang?.trim();
    }
    return lang;
  };

  const handleFileChange = (languageIndex, selectedFiles) => {
    const filesArray = Array.from(selectedFiles);

    // Initialize genres for new files
    const newGenres = {};
    filesArray.forEach((file, idx) => {
      const fileKey = `${languageIndex}_${Date.now()}_${idx}`;
      newGenres[fileKey] = {
        file: file,
        genre: "",
        tempId: fileKey,
      };
    });

    setFileLists((prev) => ({
      ...prev,
      [languageIndex]: [...(prev[languageIndex] || []), ...filesArray],
    }));

    setSongGenres((prev) => ({
      ...prev,
      [languageIndex]: [
        ...(prev[languageIndex] || []),
        ...Object.values(newGenres),
      ],
    }));
  };

  const removeFile = (languageIndex, fileIndex) => {
    setFileLists((prev) => ({
      ...prev,
      [languageIndex]: prev[languageIndex].filter((_, i) => i !== fileIndex),
    }));

    setSongGenres((prev) => ({
      ...prev,
      [languageIndex]: prev[languageIndex].filter((_, i) => i !== fileIndex),
    }));
  };

  const updateSongGenre = (languageIndex, fileIndex, genre) => {
    setSongGenres((prev) => {
      const updated = [...(prev[languageIndex] || [])];
      if (updated[fileIndex]) {
        updated[fileIndex] = {
          ...updated[fileIndex],
          genre: genre,
        };
      }
      return {
        ...prev,
        [languageIndex]: updated,
      };
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  // Handle language change with duplicate check
  const handleLanguageChange = (languageIndex, value) => {
    setValue(`languages.${languageIndex}.language`, value);
    // Reset custom language when not "other"
    if (value !== "other") {
      setValue(`languages.${languageIndex}.customLanguage`, "");
    }
  };

  // Handle custom language change with duplicate check
  const handleCustomLanguageChange = (languageIndex, value) => {
    setValue(`languages.${languageIndex}.customLanguage`, value);
  };

  const onSubmit = async (data) => {
    // Validate no empty language names
    let hasEmptyLanguage = false;
    const languageValues = [];

    for (let i = 0; i < data.languages.length; i++) {
      const lang = data.languages[i];
      let languageValue = "";

      if (lang.language === "other") {
        if (!lang.customLanguage || lang.customLanguage.trim() === "") {
          toast.error(
            `Please enter custom language name for language ${i + 1}`,
          );
          hasEmptyLanguage = true;
          break;
        }
        languageValue = lang.customLanguage.trim();
      } else if (!lang.language) {
        toast.error(`Please select a language for language ${i + 1}`);
        hasEmptyLanguage = true;
        break;
      } else {
        languageValue = lang.language;
      }

      // Check for duplicates
      const lowerCaseValue = languageValue.toLowerCase();
      if (languageValues.includes(lowerCaseValue)) {
        toast.error(
          `Duplicate language "${languageValue}" found. Please use unique languages.`,
        );
        hasEmptyLanguage = true;
        break;
      }
      languageValues.push(lowerCaseValue);
    }

    if (hasEmptyLanguage) return;

    // Validate that at least one language has files
    const hasFiles = Object.keys(fileLists).some(
      (index) => fileLists[index] && fileLists[index].length > 0,
    );

    if (!hasFiles) {
      toast.error(
        "Please upload at least one song file for at least one language",
      );
      return;
    }

    if (!imageFile) {
      toast.error("Please upload a category image");
      return;
    }

    // Validate all songs have genres
    let hasMissingGenre = false;
    Object.keys(songGenres).forEach((langIndex) => {
      songGenres[langIndex]?.forEach((song, idx) => {
        if (!song.genre || song.genre.trim() === "") {
          hasMissingGenre = true;
        }
      });
    });

    if (hasMissingGenre) {
      toast.error("Please enter genre for all songs");
      return;
    }

    // Prepare form data object for Redux
    const formDataObj = {
      name: data.name,
      image: imageFile,
      languages: data.languages
        .map((lang, index) => {
          const languageValue =
            lang.language === "other"
              ? lang.customLanguage.trim()
              : lang.language;

          // Get files with their genres
          const filesWithGenres = (songGenres[index] || [])
            .map((song, fileIndex) => ({
              file: fileLists[index]?.[fileIndex],
              genre: song.genre,
            }))
            .filter((item) => item.file);

          return {
            language: languageValue,
            songs: filesWithGenres,
          };
        })
        .filter((lang) => lang.language && lang.songs.length > 0),
    };

    // Dispatch Redux action
    try {
      const response = await dispatch(createCategory(formDataObj)).unwrap();
      if (response.success) {
        await dispatch(getAllCategories());
        toast.success("Category created successfully");
        reset({
          name: "",
          languages: [
            {
              language: "",
              customLanguage: "",
              files: [],
            },
          ],
        });

        // Reset local states
        setFileLists({});
        setSongGenres({});
        setImageFile(null);
        setImagePreview(null);
      } else {
        toast.error(response.message || "Failed to create category");
      }
    } catch (error) {
      toast.error(
        error.message || "An error occurred while creating the category",
      );
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">
        Create New Category
      </h2>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Category Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Category Name *
          </label>
          <input
            {...register("name", { required: "Category name is required" })}
            type="text"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            placeholder="e.g., Automotive, Healthcare"
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
          )}
        </div>

        {/* Category Image Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Category Image *
          </label>
          <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-orange-500 transition-colors">
            {imagePreview ? (
              <div className="relative">
                <img
                  src={imagePreview}
                  alt="Category preview"
                  className="max-h-48 rounded-lg object-contain"
                />
                <button
                  type="button"
                  onClick={removeImage}
                  className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <div className="space-y-1 text-center">
                <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                <div className="flex text-sm text-gray-600">
                  <label className="relative cursor-pointer bg-white rounded-md font-medium text-orange-600 hover:text-orange-500 focus-within:outline-none">
                    <span>Upload category image</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="sr-only"
                      onChange={handleImageChange}
                      required
                    />
                  </label>
                </div>
                <p className="text-xs text-gray-500">
                  PNG, JPG, JPEG up to 5MB
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Languages and Songs Sections */}
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <label className="block text-sm font-medium text-gray-700">
              Languages & Songs *
            </label>
            <button
              type="button"
              onClick={() =>
                append({
                  language: "",
                  customLanguage: "",
                  files: [],
                })
              }
              className="flex items-center gap-2 px-3 py-1 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
            >
              <Plus size={16} />
              Add Language
            </button>
          </div>

          {fields.map((field, languageIndex) => {
            const selectedLanguage =
              watchedLanguages?.[languageIndex]?.language;
            const customLanguage =
              watchedLanguages?.[languageIndex]?.customLanguage;
            const isDuplicate = isLanguageDuplicate(
              languageIndex,
              selectedLanguage,
              customLanguage,
            );

            return (
              <div
                key={field.id}
                className={`border rounded-lg p-4 space-y-4 relative transition-colors ${
                  isDuplicate ? "border-red-300 bg-red-50" : "border-gray-200"
                }`}
              >
                {languageIndex > 0 && (
                  <button
                    type="button"
                    onClick={() => remove(languageIndex)}
                    className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                  >
                    <Trash2 size={18} />
                  </button>
                )}

                {/* Language Select */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Language *
                  </label>
                  <select
                    {...register(`languages.${languageIndex}.language`, {
                      required: "Language is required",
                      onChange: (e) =>
                        handleLanguageChange(languageIndex, e.target.value),
                    })}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                      isDuplicate ? "border-red-500" : "border-gray-300"
                    }`}
                  >
                    <option value="">Select Language</option>
                    <option value="Hindi">Hindi</option>
                    <option value="English">English</option>
                    <option value="Marathi">Marathi</option>
                    <option value="Hindi and English Mix">
                      Hindi and English Mix
                    </option>
                    <option value="other">Other</option>
                  </select>
                  {errors.languages?.[languageIndex]?.language && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.languages[languageIndex].language.message}
                    </p>
                  )}
                  {isDuplicate && (
                    <p className="mt-1 text-sm text-red-600">
                      This language has already been selected. Please choose a
                      different language.
                    </p>
                  )}
                </div>

                {/* Custom Language Input */}
                {selectedLanguage === "other" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Enter Language Name *
                    </label>
                    <input
                      {...register(
                        `languages.${languageIndex}.customLanguage`,
                        {
                          required: "Please enter language name",
                          onChange: (e) =>
                            handleCustomLanguageChange(
                              languageIndex,
                              e.target.value,
                            ),
                        },
                      )}
                      type="text"
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                        isDuplicate ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder="e.g., Tamil, Telugu, Bengali"
                    />
                    {errors.languages?.[languageIndex]?.customLanguage && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.languages[languageIndex].customLanguage.message}
                      </p>
                    )}
                  </div>
                )}

                {/* File Upload for this language */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload Songs (MP3 files) *
                  </label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-orange-500 transition-colors">
                    <div className="space-y-1 text-center">
                      <Upload className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="flex text-sm text-gray-600">
                        <label className="relative cursor-pointer bg-white rounded-md font-medium text-orange-600 hover:text-orange-500 focus-within:outline-none">
                          <span>Upload files</span>
                          <input
                            type="file"
                            multiple
                            accept="audio/mp3,audio/mpeg"
                            className="sr-only"
                            onChange={(e) =>
                              handleFileChange(languageIndex, e.target.files)
                            }
                          />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500">
                        MP3 up to 10MB each
                      </p>
                    </div>
                  </div>

                  {/* File List with Genre Input for this language */}
                  {fileLists[languageIndex]?.length > 0 && (
                    <div className="mt-4 space-y-3">
                      <h4 className="text-sm font-medium text-gray-700">
                        Selected Files ({fileLists[languageIndex].length}):
                      </h4>
                      <div className="max-h-60 overflow-y-auto space-y-3">
                        {fileLists[languageIndex].map((file, fileIndex) => (
                          <div
                            key={fileIndex}
                            className="bg-gray-50 p-3 rounded-lg space-y-2"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2 flex-1">
                                <Music size={16} className="text-gray-500" />
                                <span className="text-sm text-gray-600 truncate">
                                  {file.name}
                                </span>
                              </div>
                              <button
                                type="button"
                                onClick={() =>
                                  removeFile(languageIndex, fileIndex)
                                }
                                className="text-red-500 hover:text-red-700"
                              >
                                <X size={18} />
                              </button>
                            </div>

                            {/* Genre Input for this song */}
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Genre *
                              </label>
                              <input
                                type="text"
                                value={
                                  songGenres[languageIndex]?.[fileIndex]
                                    ?.genre || ""
                                }
                                onChange={(e) =>
                                  updateSongGenre(
                                    languageIndex,
                                    fileIndex,
                                    e.target.value,
                                  )
                                }
                                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                placeholder="e.g., Rock, Pop, Classical, Folk"
                                required
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading || !imageFile}
          className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Creating Category..." : "Create Category"}
        </button>
      </form>
    </div>
  );
};

export default CreateCategory;
